import classNames from 'classnames';
import { FormikErrors, useFormik } from 'formik';
import { get, isEqual, pick } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { findDefaultValue } from '@/modules/common/utils/default-value.ts';
import { GIIN_REGEX, LEGAL_ENTITY_NAME_REGEX } from '@/modules/common/utils/regex.ts';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { countryOptions } from '@/modules/entity/static/countries.ts';
import { reasonOptions } from '@/modules/entity/static/no-tin-reasons.ts';
import { nonUsPersonRadioItems } from '@/modules/entity/static/non-us-person.ts';
import { TIN_HINT } from '@/modules/entity/static/tax-identification.ts';
import { Option } from '@/modules/entity/utils/option.ts';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { RadioButton, Select, SelectButtonOption, SelectButtons, TextInputField } from 'ui';
import { PlusIcon, TrashIcon } from 'ui/icons';

export interface TaxResidence {
  country: string;
  is_tin: boolean;
  tfn_or_exemption_code: string;
  reason_if_no_tin: string;
  tin: string;
}

interface TaxInformationFormik {
  account_holders_gin: string;
  sponsoring_entity_name: string;
  tax_residence: TaxResidence[];
  is_us_person: boolean;
  non_us_person_certification: string;
}

const tinOptions: Option<boolean>[] = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const defaultTaxResidence: TaxResidence = {
  country: '',
  is_tin: false,
  tfn_or_exemption_code: '',
  reason_if_no_tin: reasonOptions[0].value,
  tin: '',
};

const validationSchema = yup.object({
  account_holders_gin: yup.string().trim().matches(GIIN_REGEX, {
    message: 'Invalid GIIN format. Enter 19 characters in XXXXXX.XXXXX.XX.XXX format',
    excludeEmptyString: true,
  }),
  sponsoring_entity_name: yup
    .string()
    .trim()
    .min(2, 'Sponsoring entity name must contain at least 2 characters')
    .max(150, 'Sponsoring entity name cannot exceed 150 characters')
    .matches(LEGAL_ENTITY_NAME_REGEX, {
      message: "Use letters, numbers, spaces, and these characters only: . , & ' ( ) / # -",
      excludeEmptyString: true,
    })
    .test(
      'contains-letter',
      'Sponsoring entity name must contain at least one letter',
      value => !value || /\p{L}/u.test(value),
    ),
  tax_residence: yup.array().of(
    yup.object({
      country: yup.string().trim().required('Please choose country'),
      is_tin: yup.boolean(),
      tfn_or_exemption_code: yup
        .string()
        .trim()
        .when('country', ([country], schema) => {
          if (country === 'AUS') {
            return schema.required('This field is required');
          }
          return schema.nullable();
        }),
      tin: yup
        .string()
        .trim()
        .when('is_tin', ([is_tin], schema) => {
          if (is_tin === true) {
            return schema.required('This field is required');
          }
          return schema.nullable();
        }),
      reason_if_no_tin: yup
        .string()
        .trim()
        .when('country', ([country], schema) => {
          if (country !== 'AUS') {
            return schema.required('This field is required');
          }
          return schema.nullable();
        }),
    }),
  ),
  is_us_person: yup.boolean().required('Please choose option'),
  non_us_person_certification: yup
    .string()
    .trim()
    .when(['is_us_person'], ([is_us_person], schema) => {
      if (!is_us_person) {
        return schema.required('Please choose option');
      }
      return schema.nullable();
    }),
});

const initialValues: TaxInformationFormik = {
  account_holders_gin: '',
  sponsoring_entity_name: '',
  tax_residence: [],
  is_us_person: false,
  non_us_person_certification: nonUsPersonRadioItems[0].value,
};

export const TaxInformation = ({
  moveForward,
  entity_type_prefix,
  person_details_key,
  index,
  allowSkip,
}: OnboardingStepProps & { index?: number }) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));

  const generateInitialValues = (): TaxInformationFormik => {
    const entityTypeDetails = get(data?.entity, entity_type_prefix, {});

    if (person_details_key && (index || index === 0)) {
      const personDetails = entityTypeDetails?.[person_details_key]?.find(
        (_: any, indexOfPerson: number) => indexOfPerson === index,
      ) as TaxInformationFormik;

      return {
        ...initialValues,
        ...personDetails,
        tax_residence: personDetails?.tax_residence
          ? personDetails.tax_residence
          : [
              {
                ...defaultTaxResidence,
                country: entityTypeDetails?.country || data?.entity?.country,
              },
            ],
        is_us_person:
          personDetails?.is_us_person !== undefined
            ? personDetails.is_us_person
            : entityTypeDetails?.country === 'USA',
        non_us_person_certification:
          personDetails?.non_us_person_certification !== undefined
            ? personDetails?.non_us_person_certification
            : '',
      };
    }

    return {
      ...initialValues,
      ...entityTypeDetails,
      tax_residence: entityTypeDetails?.tax_residence
        ? entityTypeDetails.tax_residence
        : [
            {
              ...defaultTaxResidence,
              country: entityTypeDetails?.country || data?.entity?.country,
            },
          ],
      is_us_person:
        entityTypeDetails?.is_us_person !== undefined
          ? entityTypeDetails.is_us_person
          : entityTypeDetails?.country === 'USA',
      non_us_person_certification:
        entityTypeDetails?.non_us_person_certification !== undefined
          ? entityTypeDetails?.non_us_person_certification
          : '',
    };
  };

  const generatePayload = (values: TaxInformationFormik) => {
    const entityTypeDetails = data?.entity[entity_type_prefix as keyof typeof data.entity];

    if (person_details_key && (index || index === 0)) {
      return {
        ...data?.entity,
        [entity_type_prefix]: {
          ...entityTypeDetails,
          [person_details_key]: entityTypeDetails[person_details_key].map(
            (shareholder: any, indexOfShareholder: number) =>
              indexOfShareholder === index ? { ...shareholder, ...values } : shareholder,
          ),
        },
      };
    }

    return {
      ...data?.entity,
      [entity_type_prefix]: {
        ...entityTypeDetails,
        ...values,
      },
    };
  };

  const formik = useFormik<TaxInformationFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    initialValues: generateInitialValues(),
    onSubmit: async values => {
      const init = pick<TaxInformationFormik, keyof TaxInformationFormik>(values, [
        'tax_residence',
        'is_us_person',
        'non_us_person_certification',
        'sponsoring_entity_name',
        'account_holders_gin',
      ]);
      const entityTypeDetails = data?.entity[entity_type_prefix as keyof typeof data.entity];
      let personDetails: TaxInformationFormik | null = null;
      if (person_details_key && (index || index === 0)) {
        personDetails = entityTypeDetails?.[person_details_key]?.find(
          (_: any, indexOfPerson: number) => indexOfPerson === index,
        ) as TaxInformationFormik;
      }
      const current = pick<TaxInformationFormik, keyof TaxInformationFormik>(
        personDetails || entityTypeDetails,
        [
          'tax_residence',
          'is_us_person',
          'non_us_person_certification',
          'sponsoring_entity_name',
          'account_holders_gin',
        ],
      );
      if (!isEqual(init, current)) {
        await updateEntity(generatePayload(values));
      }
      moveForward && moveForward();
    },
  });

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const handleAddTaxResidence = useCallback(() => {
    formik.setFieldValue('tax_residence', [
      ...formik.values.tax_residence,
      { ...defaultTaxResidence },
    ]);
    formik.setFieldError('tax_residence', '');
  }, [formik]);

  const handleDeleteResidence = () => {
    formik.setFieldValue('tax_residence', [
      ...formik.values.tax_residence.slice(0, formik.values.tax_residence.length - 1),
    ]);
  };

  const handleChangeSelectInResidence = (
    field: string,
    index: number,
    value?: string | boolean,
  ) => {
    formik.setFieldValue(`tax_residence[${index}].${field}`, value);
    formik.setFieldError(`tax_residence[${index}].${field}`, '');
  };

  const handleChangeRadio = (field: string, value: string | boolean) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
  };

  useEffect(() => {
    const isUsPerson = formik.values.tax_residence?.some(item => item.country === 'USA');
    if (isUsPerson) {
      handleChangeRadio('is_us_person', isUsPerson);
      handleChangeRadio('non_us_person_certification', '');
    }
  }, [formik.values.tax_residence]);

  return (
    <OnboardingWrapper
      title={`${data?.entity?.entity_name ?? ''} Tax Information`}
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? moveForward : undefined}
      isLoading={isLoading}
    >
      <div className="w-full flex flex-col space-y-10">
        <div className="w-full flex flex-col space-y-6">
          <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between gap-2">
            <span className="text-4 leading-[22px]">
              Jurisdictions where the company is a tax resident
            </span>
            <button
              onClick={handleAddTaxResidence}
              className="flex items-center justify-start text-blue-600"
            >
              <img className="mr-[5px]" src={PlusIcon} alt="Loading..." />
              Add new country
            </button>
          </div>
          <div className="w-full flex flex-col space-y-2">
            {formik.values.tax_residence.map(
              ({ is_tin, country, reason_if_no_tin }, index, all) => {
                const error = formik.errors.tax_residence?.[index] as
                  | FormikErrors<TaxResidence>
                  | undefined;
                return (
                  <div
                    key={index}
                    className={classNames(
                      'w-full flex flex-col items-start justify-center space-y-4 py-6',
                      // 'rounded-lg border border-gray-900 border-opacity-16 px-4',
                    )}
                  >
                    <div className="w-full flex items-center justify-between">
                      <p className="text-[14px] leading-[22px]">
                        Country of Tax Residence {index + 1}
                      </p>
                      {index === all.length - 1 && (
                        <button onClick={handleDeleteResidence}>
                          <img src={TrashIcon} alt="Delete" />
                        </button>
                      )}
                    </div>
                    <div className="w-full flex sm:flex-row flex-col items-start justify-between sm:gap-4 gap-2">
                      <Select
                        isClearable
                        name="country"
                        label="country"
                        options={countryOptions}
                        value={country}
                        onChange={value =>
                          handleChangeSelectInResidence('country', index, value || undefined)
                        }
                        error={error?.country}
                      />
                      {country === 'AUS' ? (
                        <TextInputField
                          label="TIN"
                          hintLabel={TIN_HINT}
                          name={`tax_residence[${index}].tfn_or_exemption_code`}
                        />
                      ) : (
                        <>
                          <SelectButtons<boolean>
                            label="do you have tin?"
                            hintLabel={TIN_HINT}
                            options={tinOptions}
                            selected={
                              findDefaultValue(
                                tinOptions,
                                is_tin || false,
                              ) as SelectButtonOption<boolean>
                            }
                            onChange={selected =>
                              handleChangeSelectInResidence('is_tin', index, selected)
                            }
                          />
                          {is_tin ? (
                            <TextInputField
                              name={`tax_residence[${index}].tin`}
                              label="TIN"
                              hintLabel={TIN_HINT}
                            />
                          ) : (
                            <Select
                              isClearable
                              options={reasonOptions}
                              label="reason if no tin"
                              value={reason_if_no_tin}
                              onChange={value =>
                                handleChangeSelectInResidence(
                                  'reason_if_no_tin',
                                  index,
                                  value || undefined,
                                )
                              }
                              error={error?.reason_if_no_tin}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              },
            )}
          </div>
        </div>
        <div className="w-full flex flex-col space-y-4">
          <p className="text-[14px] leading-[22px]">
            Is the Account Holder a specified U.S. person? A U.S. person includes a U.S. citizen or
            resident alien of the U.S. even if residing outside the U.S.
          </p>
          <RadioButton
            items={tinOptions}
            value={formik.values.is_us_person}
            onChange={value => {
              handleChangeRadio('is_us_person', value);
              if (value === true) {
                handleChangeRadio('non_us_person_certification', '');
              }
            }}
          />
        </div>
        {!formik.values.is_us_person && (
          <div className="w-full flex flex-col space-y-4">
            <div className="w-full flex flex-col space-y-2">
              <p className="text-4 leading-[22px]">Non U.S Person Certification</p>
              <p className="text-3 leading-5 text-gray-600">
                Select only a single category. Select a classification that matches your FATCA
                status:
              </p>
            </div>
            <RadioButton
              value={formik.values.non_us_person_certification}
              onChange={value => handleChangeRadio('non_us_person_certification', value)}
              items={nonUsPersonRadioItems}
              error={formik.errors.non_us_person_certification}
            />
          </div>
        )}
        <div className="w-full flex flex-col space-y-8">
          <TextInputField
            label="Account Holder’s GIIN (Optional)"
            name="account_holders_gin"
            placeholder="XXXXXX.XXXXX.XX.XXX"
            limit={19}
            hintLabel={
              'Please enter the Global Intermediary Identification Number (GIIN) for the account\n' +
              '            holder, if applicable. This field is optional and can be left blank if the account\n' +
              '            holder does not have a GIIN.'
            }
          />
          <TextInputField
            label="Sponsoring Entity’s Name (optional)"
            name="sponsoring_entity_name"
            placeholder="Example Sponsor Pty Ltd"
            limit={150}
            hintLabel={
              'Please enter the full legal name of the sponsoring entity, if applicable. This field is\n' +
              '            optional and can be left blank if there is no sponsoring entity.'
            }
          />
        </div>
      </div>
    </OnboardingWrapper>
  );
};
